/*jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import SelectorTools from 'app/utility/SelectorTools.js';
import numeral from 'numeral';

const ObserverSupported = (typeof IntersectionObserver === 'function') ? true : false;

class ImportItemDisplay extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            imageInvalid: false,
            inViewport: false,
            observer: null,
            isImageLoading: false
        };

        this.imageContainerRef = React.createRef();

        this.observerCallback = this.observerCallback.bind(this);
        this.setImageInvalid = this.setImageInvalid.bind(this);
        this.setUpObserver = this.setUpObserver.bind(this);

    }

    componentDidMount() {
        let item = this.props.item.item;

        if (!item) {
            item = this.props.item;
        }

        if (item) {
            if ((item.thumbLink !== undefined && item.thumbLink !== '') ||
                (item.external_media !== undefined && item.external_media.length && item.external_media[0].type !== 'video') ||
                (item.preview !== undefined && item.preview.itemInfo !== undefined && item.preview.itemInfo.imageLink)) {
                this.setState({
                    isImageLoading: true
                });
            }
        }

        if (this.imageContainerRef.current) {
            this.setUpObserver();
        }

    }

    componentDidUpdate(prevProps, prevState) {
        this.setUpObserver();
    }

    componentWillUnmount() {
        if (this.state.observer !== null) {
            this.state.observer.disconnect();
        }
    }

    setUpObserver() {
        if (this.imageContainerRef.current && this.state.observer === null && ObserverSupported) {
            let observer = new IntersectionObserver(this.observerCallback, {
                threshold: 0.5
            });

            observer.observe(this.imageContainerRef.current);

            this.setState({
                observer
            });
        } else {
            // if the browser doesn't support IntersectionObserver because it's old
            // just set the inViewport to true and let it load all the images
            if (!ObserverSupported && !this.state.inViewport) {
                this.setState({
                    inViewport: true
                });
            }

        }
    }

    setImageInvalid() {
        this.setState({
            imageInvalid: true,
            isImageLoading: false
        });
    }

    observerCallback(entries) {

        entries.map((entry) => {
            if (entry.intersectionRatio > 0 && this.imageContainerRef.current && !this.state.inViewport) {
                this.state.observer.unobserve(this.imageContainerRef.current);
                this.state.observer.disconnect();
                this.setState({
                    inViewport: true,
                    observer: null
                });

            }
        });

    }

    render() {
        let image = null,
            copy = null,
            source = null,
            stats = null,
            item = this.props.item.item,
            gridSelectors = new SelectorTools({
                'item': 'grid-item',
                'selector': 'grid-selector',
                'copy': 'grid-copy',
                'source': 'grid-source',
                'image': 'grid-image',
                'stats': 'grid-stats',
                'likes': 'grid-likes',
                'shares': 'grid-shares',
                'selectionControl': 'grid-selector',
                'infoStrip': 'info-strip',
                'imgWarning': 'img-warning'
            }),
            listSelectors = new SelectorTools({
                'item': 'list-item',
                'selector': 'list-selector',
                'copy': 'list-copy',
                'source': 'list-source',
                'image': 'list-image',
                'stats': 'list-stats',
                'likes': 'list-likes',
                'shares': 'list-shares',
                'selectionControl': 'list-selector',
                'infoStrip': 'info-strip',
                'imgWarning': 'img-warning'
            }),
            constants = this.props.app.config.constants,
            sourceName = '',
            likes = null,
            shares = null,
            selectors = null,
            selectionControl = null,
            loading = (
                <div className="working">
                    <i className="fa fa-circle-o-notch fa-spin"></i>
                </div>
            );

        selectors = (this.props.displayType === 'grid') ? gridSelectors : listSelectors;

        if (!item) {
            item = this.props.item;
        }

        if (this.props.selected) {
            selectors.append('selectionControl', 'active');
        }

        if (item.thumbLink !== undefined && item.thumbLink !== '' && !this.state.imageInvalid) {
            image = (
                <div className={selectors.image}>
                    <img src={item.thumbLink} onLoad={() => { this.setState({isImageLoading: false}) }} onError={this.setImageInvalid}/>
                    {this.state.isImageLoading ? (
                        loading
                    ) : (
                        null
                    )}
                </div>
            );
        } else if (item.external_media !== undefined && item.external_media.length && !this.state.imageInvalid) {
            let property = 'src',
                imageContent = null;
            if (item.external_media[0].type !== undefined && item.external_media[0].type === 'video') {
                property = 'thumb';
            }

            if (item.external_media[0].type === 'video' && (item.external_media[0][property] === undefined || !item.external_media[0][property])) {
                imageContent = (
                    <div className="video-default">
                        <i className="icon ion-ios-videocam"/>
                    </div>
                );
            } else {
                imageContent = (
                    <img src={item.external_media[0][property]} onLoad={() => { this.setState({isImageLoading: false}) }} onError={this.setImageInvalid}/>
                );
            }
            image = (
                <div className={selectors.image}>
                    {imageContent}
                    {this.state.isImageLoading ? (
                        loading
                    ) : (
                        null
                    )}
                </div>
            );

        } else if (item.preview !== undefined && item.preview.itemInfo !== undefined && item.preview.itemInfo.imageLink) {
            image = (
                <div className={selectors.image}>
                    <img src={item.preview.itemInfo.imageLink} onLoad={() => { this.setState({isImageLoading: false}) }}  onError={this.setImageInvalid}/>
                    {this.state.isImageLoading ? (
                        loading
                    ) : (
                        null
                    )}
                </div>
            );
        }

        let imgWarning = null;
        if (item.imgErrorMsg !== undefined && item.imgErrorMsg !== "" ||
            item.urlErrorMsg !== undefined && item.urlErrorMsg !== "") {
            if (item.imgErrorMsg.match("valid")) {
                image = null;
                selectors.append('imgWarning', 'solo');
            }

            let imgError = null;
            if (item.imgErrorMsg !== undefined && item.imgErrorMsg !== "") {
                imgError = (
                    <p>This item's image will not post: {item.imgErrorMsg}</p>
                );
            }

            let urlError = null;
            if (item.urlErrorMsg !== undefined && item.urlErrorMsg !== "") {
                urlError = (
                    <p>This item's URL had an error: {item.urlErrorMsg}</p>
                );
            }

            imgWarning = (
                <div className={selectors.imgWarning}>
                   {imgError}
                   {urlError}
                </div>
            );
        }

        if (item.title) {
            if (image === null && this.props.context !== 'post-analyzer') {
                selectors.append('copy', 'solo');
            }
            copy = (
                <div className={selectors.copy}>
                    {item.title} <span className="item-url">{item.longUrl}</span>
                </div>
            );
        } else if (!item.title && item.comment) {
            if (image === null && this.props.context !== 'post-analyzer') {
                selectors.append('copy', 'solo');
            }
            copy = (
                <div className={selectors.copy}>
                    {item.comment} <span className="item-url">{item.longUrl}</span>
                </div>
            );
        } else if (item.preview !== undefined && item.preview) {
            if (image === null && this.props.context !== 'post-analyzer') {
                selectors.append('copy', 'solo');
            }
            if (item.preview.message) {
                copy = (
                    <div className={selectors.copy}>
                        {item.preview.message} <span className="item-url">{item.preview.longUrl}</span>
                    </div>
                );
            } else if (item.preview.itemInfo !== undefined && item.preview.itemInfo.title) {
                copy = (
                    <div className={selectors.copy}>
                        {item.preview.itemInfo.title} <span className="item-url">{item.preview.longUrl}</span>
                    </div>
                );
            }
        }

        if (item.item_stats) {
            switch(parseInt(this.props.service, 10)) {
                case constants.BULK_SVC_TWITTER:
                    likes = (
                        <span className={selectors.likes}>
                            <i className="icon ion-heart"></i>
                            {numeral(item.item_stats.favorite_count).format('0[.]0a')}
                        </span>
                    );
                    shares = (
                        <span className={selectors.shares}>
                            <i className="fa fa-retweet"></i>
                            {numeral(item.item_stats.retweet_count).format('0[.]0a')}
                        </span>
                    );
                    sourceName = "@" + item.item_stats.source;
                    break;

                case constants.BULK_SVC_INSTAGRAM:
                    likes = (
                        <span className={selectors.likes}>
                            <i className="icon ion-heart"></i>
                            {numeral(item.item_stats.likes).format('0[.]0a')}
                        </span>
                    );
                    sourceName = "@" + item.item_stats.source;
                    break;

                case constants.BULK_SVC_FEED:
                    selectors.append('source', 'full');
                    sourceName = item.item_stats.source;
                    break;

                default:
                    break;
            }

            stats = (
                <div className={selectors.stats}>
                    {likes}
                    {shares}
                </div>
            );
            source = (
                <div className={selectors.source}>
                    {sourceName}
                </div>
            );
        }

        // item is coming from the post analyzer
        if (item.insights) {
            let impressions = null,
                rollover = null;
            if (item.impressions !== undefined) {
                impressions = (
                    <span className={selectors.shares}>
                        <i className="icon ion-eye"></i>
                        {numeral(item.impressions).format('0[.]0a')}
                    </span>
                );
            }
            likes = (
                <span className={selectors.likes}>
                    <i className="icon ion-heart"></i>
                    {numeral(item.insights.likes).format('0[.]0a')}
                </span>
            );
            if (this.props.recentPostRolloverIndex === this.props.itemIndex) {
                rollover = this.props.rolloverElement;
            }
            stats = (
                <div className={selectors.stats}
                    onMouseEnter={(e) => {
                        this.props.toggleRollver('recent', this.props.itemIndex);
                    }}
                    onMouseLeave={(e) => {
                        this.props.toggleRollver('recent', -1);
                    }}
                    onClick={(e) => {
                        this.props.toggleRollver('recent', this.props.itemIndex);
                    }}>
                    {rollover}
                    {impressions}
                    {likes}
                </div>
            );
        }

        if (this.props.displayType === 'grid' && this.props.context === 'importer') {
            selectionControl = (
                <div className={selectors.selectionControl}>
                    <i className="icon ion-checkmark"></i>
                </div>
            );
        } else if (this.props.context === 'importer') {
            let selectionIcon = null;
            if (selectors.selectionControl.match('active')) {
                selectionIcon = (<i className="icon ion-checkmark"></i>);
            }
            selectionControl = (
                <div className={selectors.selectionControl}>
                    <div className="round-button">{selectionIcon}</div>
                    <div className="selection-label">Select</div>
                </div>
            )
        }

        if (image === null && this.props.context !== 'post-analyzer') {
            selectors.append('infoStrip', 'solo');
        } else if (image === null && this.props.context === 'post-analyzer') {
            // sets up a placeholder
            image = <div className={selectors.image}/>;
        }

        let categoryStrip = null;
        if (this.props.context === 'importer') {
            categoryStrip = (
                <div className="category-strip"></div>
            );
        }

        if (this.state.inViewport === false) {
            image = <div className={selectors.image} ref={this.imageContainerRef}/>;
        }

        let shareButton = null;
        if (this.props.context === 'post-analyzer') {
            shareButton = (
                <div className="share-button" role="button" arial-label="Share This Item Again" onClick={this.props.onShare}>
                    Share
                </div>
            )
        }

        return (
            <div className={selectors.item} onClick={this.props.onItemSelect} data-id={this.props.item.id} role="button" aria-label="choose item">
                {selectionControl}
                {image}
                {copy}
                {imgWarning}
                <div className={selectors.infoStrip}>
                    {source}
                    {stats}
                    {shareButton}
                </div>
                {categoryStrip}
            </div>
        );
    }
}

ImportItemDisplay.displayName = "ImportItemDisplay";

ImportItemDisplay.propTypes = {
    item: PropTypes.object,
    displayType: PropTypes.string,
    context: PropTypes.string,
    rolloverElement: PropTypes.object
};

ImportItemDisplay.defaultProps = {
    item: {},
    displayType: 'grid',
    context: 'importer',
    rolloverElement: null
};

export default ImportItemDisplay;
